import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { client } from "@src/apolloClient";
import {
  PodcastMediaType,
  PodcastStatus,
  UploadPodcastEpisodeDocument,
  type UploadPodcastEpisodeMutation,
  type UploadPodcastEpisodeMutationVariables,
} from "@src/gql/generated";
import { uploadPodcastCover, uploadPodcastMedia } from "@src/pages/podcasts/utils/uploadPodcastMedia";
import { UploadCancelledError } from "@src/utils/helpers/upload-post/multipartUpload";
import type { PodcastEpisode } from "@src/pages/podcasts/components/types";

export interface PodcastUploadFields {
  title: string;
  description?: string;
  category?: string;
  tags: string[];
  mediaType: PodcastMediaType;
  coverFile?: File | null;
  durationSeconds?: number;
}

export type PodcastUploadStatus = "uploading" | "creating-episode" | "success" | "error" | "cancelled";

export interface PodcastUploadJob {
  id: string;
  file: File;
  fileName: string;
  fields: PodcastUploadFields;
  progress: number;
  status: PodcastUploadStatus;
  error?: string;
  episode?: PodcastEpisode;
}

type EpisodeCreatedListener = (episode: PodcastEpisode) => void;

interface PodcastUploadContextValue {
  jobs: PodcastUploadJob[];
  hasActiveUploads: boolean;
  startPodcastUpload: (file: File, fields: PodcastUploadFields) => string;
  retryPodcastUpload: (jobId: string) => void;
  cancelPodcastUpload: (jobId: string) => void;
  dismissPodcastUpload: (jobId: string) => void;
  /** Subscribe to newly created episodes (e.g. to prepend to a visible list). Returns unsubscribe. */
  onEpisodeCreated: (listener: EpisodeCreatedListener) => () => void;
}

const PodcastUploadContext = createContext<PodcastUploadContextValue | null>(null);

// Cover art is tiny next to the episode file; give it a sliver of the progress bar.
const COVER_PROGRESS_SHARE = 5;
const CREATE_PROGRESS_SHARE = 2;

const isActive = (job: PodcastUploadJob) => job.status === "uploading" || job.status === "creating-episode";

/**
 * Background podcast upload manager, mounted once at the app root.
 *
 * Jobs run in plain async functions against the module-level Apollo client — not component
 * hooks — so closing the upload modal or navigating between routes never cancels them.
 * They do NOT survive a full page reload/tab close (no resumable upload state is persisted);
 * a beforeunload prompt warns the user while anything is in flight.
 */
export function PodcastUploadProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<PodcastUploadJob[]>([]);
  const listenersRef = useRef(new Set<EpisodeCreatedListener>());
  const controllersRef = useRef(new Map<string, AbortController>());

  const patchJob = useCallback((jobId: string, patch: Partial<PodcastUploadJob>) => {
    setJobs((prev) => prev.map((job) => (job.id === jobId ? { ...job, ...patch } : job)));
  }, []);

  const runJob = useCallback(
    async (jobId: string, file: File, fields: PodcastUploadFields) => {
      patchJob(jobId, { status: "uploading", progress: 0, error: undefined });
      const controller = new AbortController();
      controllersRef.current.set(jobId, controller);
      try {
        let coverImageUrl: string | undefined;
        if (fields.coverFile) {
          coverImageUrl = (await uploadPodcastCover(fields.coverFile)).fileUrl;
        }
        if (controller.signal.aborted) throw new UploadCancelledError();

        const mediaStart = fields.coverFile ? COVER_PROGRESS_SHARE : 0;
        const mediaShare = 100 - mediaStart - CREATE_PROGRESS_SHARE;

        let lastReported = -1;
        const { fileUrl } = await uploadPodcastMedia(
          file,
          fields.mediaType,
          (percent) => {
            const overall = Math.floor(mediaStart + (percent / 100) * mediaShare);
            if (overall !== lastReported) {
              lastReported = overall;
              patchJob(jobId, { progress: overall });
            }
          },
          controller.signal,
        );

        patchJob(jobId, { status: "creating-episode", progress: 100 - CREATE_PROGRESS_SHARE });

        const { data } = await client.mutate<UploadPodcastEpisodeMutation, UploadPodcastEpisodeMutationVariables>({
          mutation: UploadPodcastEpisodeDocument,
          variables: {
            input: {
              rawUrl: fileUrl,
              mediaType: fields.mediaType,
              title: fields.title,
              description: fields.description || undefined,
              category: fields.category || undefined,
              tags: fields.tags.length ? fields.tags : undefined,
              coverImageUrl,
              durationSeconds: fields.durationSeconds,
            },
          },
          fetchPolicy: "no-cache",
        });

        const episode = data?.uploadPodcastEpisode;
        if (!episode) throw new Error("The server did not return the new episode");

        patchJob(jobId, { status: "success", progress: 100, episode });
        listenersRef.current.forEach((listener) => listener(episode));

        toast.success(
          episode.status === PodcastStatus.Ready
            ? `"${episode.title}" is live`
            : `"${episode.title}" uploaded — it will be playable once processing finishes`,
        );
      } catch (error) {
        if (error instanceof UploadCancelledError) {
          patchJob(jobId, { status: "cancelled" });
        } else {
          const message = error instanceof Error ? error.message : "Upload failed";
          patchJob(jobId, { status: "error", error: message });
          toast.error(`Podcast upload failed: ${message}`);
        }
      } finally {
        controllersRef.current.delete(jobId);
      }
    },
    [patchJob],
  );

  const startPodcastUpload = useCallback(
    (file: File, fields: PodcastUploadFields) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setJobs((prev) => [...prev, { id, file, fileName: file.name, fields, progress: 0, status: "uploading" }]);
      void runJob(id, file, fields);
      return id;
    },
    [runJob],
  );

  const retryPodcastUpload = useCallback(
    (jobId: string) => {
      const job = jobs.find((j) => j.id === jobId);
      if (!job || (job.status !== "error" && job.status !== "cancelled")) return;
      // Same in-memory File — no need to re-select it.
      void runJob(job.id, job.file, job.fields);
    },
    [jobs, runJob],
  );

  const cancelPodcastUpload = useCallback((jobId: string) => {
    controllersRef.current.get(jobId)?.abort();
  }, []);

  const dismissPodcastUpload = useCallback((jobId: string) => {
    setJobs((prev) => prev.filter((job) => job.id !== jobId || isActive(job)));
  }, []);

  const onEpisodeCreated = useCallback((listener: EpisodeCreatedListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const hasActiveUploads = jobs.some(isActive);

  useEffect(() => {
    if (!hasActiveUploads) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasActiveUploads]);

  // Successful jobs clear themselves after a short while.
  useEffect(() => {
    const finished = jobs.filter((job) => job.status === "success");
    if (finished.length === 0) return;
    const timer = setTimeout(() => {
      setJobs((prev) => prev.filter((job) => !finished.some((f) => f.id === job.id)));
    }, 6000);
    return () => clearTimeout(timer);
  }, [jobs]);

  const value = useMemo(
    () => ({
      jobs,
      hasActiveUploads,
      startPodcastUpload,
      retryPodcastUpload,
      cancelPodcastUpload,
      dismissPodcastUpload,
      onEpisodeCreated,
    }),
    [
      jobs,
      hasActiveUploads,
      startPodcastUpload,
      retryPodcastUpload,
      cancelPodcastUpload,
      dismissPodcastUpload,
      onEpisodeCreated,
    ],
  );

  return <PodcastUploadContext.Provider value={value}>{children}</PodcastUploadContext.Provider>;
}

export function usePodcastUploads(): PodcastUploadContextValue {
  const ctx = useContext(PodcastUploadContext);
  if (!ctx) throw new Error("usePodcastUploads must be used inside PodcastUploadProvider");
  return ctx;
}
